import { ref, computed, getCurrentInstance } from 'vue';
import { debounce } from 'lodash';
import { verifyAdminUser } from '@/components/helpers';
import WebStorage from '@/common/WebStorage';

export function useGenericHelpers() {
	const instance = getCurrentInstance();
	const proxy = instance?.proxy as any;
	const store = proxy?.$store;
	const route = proxy?.$route;

	const loadingService = ref<any>(null);
	const debouncedFunctions = ref<any[]>([]);

	const isReadOnly = computed((): boolean => {
		if (
			['NodeViewExisting'].includes(route?.name as string) &&
			store?.getters.workflowPermissionEdit &&
			store?.getters.workflowAtivo &&
			(route?.params.mode != 'Visualizar' || route?.params.mode == '') ||
			['NodeViewNew'].includes(route?.name as string) || 
			['EditVersionById'].includes(route?.name as string)
		) {
			return false;
		}
		return true;
	});

	const viewMode = computed((): boolean => {
		return route?.params.mode == 'Visualizar' ? true : false;
	});

	const userN0 = computed(() => {
		const tokenData = JSON.parse(WebStorage.getItem('tokenData') || '{}');
		return tokenData.Level === 'N0';
	});

	const openShareModal = (data: any, mode: string, viewMode?: boolean) => {
		let payload;
		if (typeof data.id == 'string') {
			payload = {
				id: [data?.id],
				mode,
				viewMode,
				owner: {
					id: data?.usuarioCriador?.id || data?.usuarioCriadorId,
					nome: data?.nome,
					nomeProjeto: data?.projeto?.nome || data?.nomeProjeto,
				},
			};
		} else if (typeof data.id == 'object') {
			payload = { id: [...data?.id], mode };
		}

		store.dispatch('ui/openShareModal', payload);
	};

	const displayTimer = (msPassed: number, showMs = false): string => {
		if (msPassed < 60000) {
			if (showMs === false) {
				return `${Math.floor(msPassed / 1000)} ${proxy?.$locale.baseText('genericHelpers.sec')}`;
			}

			return `${msPassed / 1000} ${proxy?.$locale.baseText('genericHelpers.sec')}`;
		}

		const secondsPassed = Math.floor(msPassed / 1000);
		const minutesPassed = Math.floor(secondsPassed / 60);
		const secondsLeft = (secondsPassed - minutesPassed * 60).toString().padStart(2, '0');

		return `${minutesPassed}:${secondsLeft} ${proxy?.$locale.baseText('genericHelpers.min')}`;
	};

	const initializeTrackSale = () => {
		(async function (f: Document, b: string, g: string) {
			const adminUser = await verifyAdminUser();
			if (adminUser != false) return;

			const usuario = JSON.parse(WebStorage.getItem('userInfo') || '{}');

			const a = {
				key: 'a0f2a72058f971a4',
				campaign: '44f2ab62',
				name: usuario.person,
				email: usuario.email,
				identification: usuario.email,
				tags: {
					'Nome da Conta': WebStorage.getItem('accountName'),
					'ID Conta': usuario.identificationDocument,
				},
				salute: `${usuario.person}, tudo bem?`,
				thanks: `Obrigado, ${usuario.nome}!`,
			};

			let e: any;
			const c = f.getElementsByTagName(b)[0];
			if (f.getElementById(g)) return;
			e = f.createElement(b);
			e.id = g;
			e.src = 'https://cdn.tracksale.co/tracksale-js/tracksale.js';
			e.type = 'text/javascript';
			e.async = true;
			e.onload = e.onreadystatechange = function () {
				const h = this.readyState;
				if (h && h !== 'complete' && h !== 'loaded') return;
				try {
					const d = new (window as any).Tracksale();
					d.init(a);
				} catch (i) {
					console.error('Erro ao inicializar Tracksale:', i);
				}
			};
			if (c.parentNode) {
				c.parentNode.insertBefore(e, c);
			}
		})(document, 'script', 'tracksale-js');
	};

	const editAllowedCheck = (): boolean => {
		if (isReadOnly.value) {
			store?.commit('activeAlert', {
				message: 'O fluxo está em modo de visualização e portanto não pode ser editado.',
				status: 'error',
			});

			return false;
		}
		return true;
	};

	const startLoading = (text?: string) => {
		if (loadingService.value !== null) {
			return;
		}

		loadingService.value = proxy?.$loading({
			lock: true,
			text: text || proxy?.$locale.baseText('genericHelpers.loading'),
			background: 'rgba(28, 38, 56, 0,6)',
		});
	};

	const setLoadingText = (text: string) => {
		if (loadingService.value) {
			loadingService.value.text = text;
		}
	};

	const stopLoading = () => {
		if (loadingService.value !== null) {
			loadingService.value.close();
			loadingService.value = null;
		}
	};

	const callDebounced = async (...inputParameters: any[]): Promise<void> => {
		const functionName = inputParameters.shift() as string;
		const debounceTime = inputParameters.shift() as number;

		if ((debouncedFunctions.value as any)[functionName] === undefined) {
			(debouncedFunctions.value as any)[functionName] = debounce(
				proxy?.[functionName],
				debounceTime,
				{ leading: true }
			);
		}
		await (debouncedFunctions.value as any)[functionName].apply(proxy, inputParameters);
	};

	return {
		loadingService,
		debouncedFunctions,
		isReadOnly,
		viewMode,
		userN0,
		openShareModal,
		displayTimer,
		initializeTrackSale,
		editAllowedCheck,
		startLoading,
		setLoadingText,
		stopLoading,
		callDebounced,
	};
}
